/**
 * © 2026 Madar.iq — جميع الحقوق محفوظة
 * هذا الملف جزء من منظومة Madar.iq البرمجية.
 * يُحظر نسخه أو تعديله أو توزيعه دون إذن كتابي.
 * All Rights Reserved — Unauthorized use is prohibited.
 */
import React, { useState, useEffect } from "react"; // Final design touch 2
import { motion } from "framer-motion";
import {
  FiDollarSign,
  FiTruck,
  FiPieChart,
  FiEye,
  FiChevronDown
} from "react-icons/fi";
// Recharts imports removed for custom spark design
import { useUI } from "../../theme/ThemeProvider";
import { useNavigate } from "react-router-dom";
import { formatNumber } from "../../../utils/formatNumber";

// --- Helper Functions ---
const parseOrderDate = (value) => {
  if (!value) return null;
  const d = new Date(value.toString().replace(/(\d)(AM|PM)/, "$1 $2"));
  return isNaN(d.getTime()) ? null : d;
};

const getOrderTotal = (order) => {
  return Number(order.totalAmount || 0) ||
    (Array.isArray(order.details) ? order.details.reduce((sum, item) => sum + Number(item.lineTotal || 0), 0) : 0);
};

const getOrderNetProfit = (order) => {
  const candidates = [
    order.netAmount,
    order.netProfit,
    order.profit,
    order.profitAmount,
  ];
  for (const value of candidates) {
    const n = Number(value);
    if (!Number.isNaN(n) && n !== 0) return n;
  }
  return getOrderTotal(order);
};

const formatToApiDate = (date) => {
  if (!date) return "";
  const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
  const m = months[date.getMonth()];
  const d = date.getDate();
  const y = date.getFullYear();
  let hrs = date.getHours();
  const mins = date.getMinutes().toString().padStart(2, "0");
  const ampm = hrs >= 12 ? "PM" : "AM";
  hrs = hrs % 12 || 12;
  return `${m} ${d} ${y} ${hrs}:${mins}${ampm}`;
};

const formatToIsoDateLocal = (date) => {
  if (!date) return "";
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

// --- Module-level cache (survives re-renders, wiped on tab close) ---
// Key pattern: `sales_report__<type>__<filter>`
const _memCache = {};

const cacheKey = (type, filter) => `sales_report__${type}__${filter}`;

const readCache = (type, filter) => {
  const k = cacheKey(type, filter);
  if (_memCache[k] !== undefined) return _memCache[k];
  try {
    const raw = localStorage.getItem(k);
    if (raw !== null) { _memCache[k] = JSON.parse(raw); return _memCache[k]; }
  } catch (_) {}
  return null;
};

const writeCache = (type, filter, value) => {
  const k = cacheKey(type, filter);
  _memCache[k] = value;
  try { localStorage.setItem(k, JSON.stringify(value)); } catch (_) {}
};


// --- Stat Card Component ---
const StatCard = ({ title, icon: Icon, type, allOrders, visitsCount, isArabic, isDark, navigate, colorScheme, baseUrl, tenantId }) => {
  const [filter, setFilter] = useState("all");

  // Seed initial value from cache so the number shows immediately
  const [data, setData] = useState(() => {
    const cached = (type === 'sales' || type === 'profits' || type === 'delivery') ? readCache(type, "all") : null;
    return { value: cached ?? 0, prevValue: 0, trend: 0, sparkData: [] };
  });
  const [loading, setLoading] = useState(false);

  const computeLocalValue = (ordersList, selectedFilter) => {
    const now = new Date();
    const rangeStart = new Date();
    if (selectedFilter === "1d") {
      rangeStart.setHours(0, 0, 0, 0);
    } else if (selectedFilter === "7d") {
      rangeStart.setDate(now.getDate() - 7);
    } else if (selectedFilter === "30d") {
      rangeStart.setDate(now.getDate() - 30);
    } else if (selectedFilter === "60d") {
      rangeStart.setDate(now.getDate() - 60);
    } else if (selectedFilter === "90d") {
      rangeStart.setDate(now.getDate() - 90);
    } else if (selectedFilter === "1y") {
      rangeStart.setFullYear(now.getFullYear() - 1);
    } else {
      rangeStart.setTime(0);
    }

    const filtered = ordersList.filter((o) => {
      const d = parseOrderDate(o.orderDate || o.OrderDate || o.createdAt);
      return d && d >= rangeStart;
    });

    if (type === "sales") return filtered.reduce((sum, o) => sum + getOrderTotal(o), 0);
    if (type === "profits") return filtered.reduce((sum, o) => sum + getOrderNetProfit(o), 0);
    if (type === "delivery") return filtered.reduce((sum, o) => sum + Number(o.deliveryPrice || 0), 0);
    return visitsCount;
  };

  useEffect(() => {
    let isMounted = true;

    const fetchDataAndTrends = async () => {
      setLoading(true);
      const now = new Date();
      let startDate = new Date();
      let prevStartDate = new Date();
      let prevEndDate = new Date();

      if (filter === "1d") {
        startDate.setHours(0, 0, 0, 0);
        prevStartDate.setDate(now.getDate() - 1);
        prevStartDate.setHours(0, 0, 0, 0);
        prevEndDate.setDate(now.getDate() - 1);
        prevEndDate.setHours(23, 59, 59, 999);
      } else if (filter === "7d") {
        startDate.setDate(now.getDate() - 7);
        prevStartDate.setDate(now.getDate() - 14);
        prevEndDate.setDate(now.getDate() - 7);
      } else if (filter === "30d") {
        startDate.setDate(now.getDate() - 30);
        prevStartDate.setDate(now.getDate() - 60);
        prevEndDate.setDate(now.getDate() - 30);
      } else if (filter === "60d") {
        startDate.setDate(now.getDate() - 60);
        prevStartDate.setDate(now.getDate() - 120);
        prevEndDate.setDate(now.getDate() - 60);
      } else if (filter === "90d") {
        startDate.setDate(now.getDate() - 90);
        prevStartDate.setDate(now.getDate() - 180);
        prevEndDate.setDate(now.getDate() - 90);
      } else if (filter === "1y") {
        startDate.setFullYear(now.getFullYear() - 1);
        prevStartDate.setFullYear(now.getFullYear() - 2);
        prevEndDate.setFullYear(now.getFullYear() - 1);
      } else {
        startDate = new Date(0);
        prevStartDate = new Date(0);
        prevEndDate = new Date(0);
      }

      const filtered = allOrders.filter(o => {
        const d = parseOrderDate(o.orderDate || o.OrderDate || o.createdAt);
        return d && d >= startDate;
      });

      const prevFiltered = allOrders.filter(o => {
        const d = parseOrderDate(o.orderDate || o.OrderDate || o.createdAt);
        return d && d >= prevStartDate && d < prevEndDate;
      });

      const calculateSum = (ordersList, fieldType) => {
        if (fieldType === 'sales') return ordersList.reduce((acc, o) => acc + getOrderTotal(o), 0);
        if (fieldType === 'profits') return ordersList.reduce((acc, o) => acc + getOrderNetProfit(o), 0);
        if (fieldType === 'delivery') return ordersList.reduce((acc, o) => acc + Number(o.deliveryPrice || 0), 0);
        if (fieldType === 'visits') return visitsCount;
        return 0;
      };

      let val = calculateSum(filtered, type);
      let prevVal = calculateSum(prevFiltered, type);

      // --- Parallel API calls for Sales, Profits, and Delivery Fees ---
      if (type === 'sales' || type === 'profits' || type === 'delivery') {
        try {
          const token = localStorage.getItem("authToken") || localStorage.getItem("token") || "";
          const headers = token ? { "Authorization": `Bearer ${token}`, "Accept": "application/json" } : { "Accept": "application/json" };

          const buildUrl = (start, end) => {
            if (type === 'delivery') {
              let url = `${baseUrl}/TenantDelivery/GetTotalFees?tenantId=${tenantId}`;
              if (filter !== "all" && start && end) {
                url += `&fromDate=${encodeURIComponent(formatToIsoDateLocal(start))}&toDate=${encodeURIComponent(formatToIsoDateLocal(end))}`;
              }
              return url;
            }

            let url = `${baseUrl}/ProfitReports/total?tenantId=${tenantId}`;
            if (filter !== "all" && start && end) {
              url += `&fromDate=${encodeURIComponent(formatToApiDate(start))}&toDate=${encodeURIComponent(formatToApiDate(end))}`;
            }
            return url;
          };

          const extractValue = (resData) =>
            type === 'sales'
              ? (resData?.TotalSales ?? resData?.totalSales ?? null)
              : type === 'profits'
                ? (resData?.TotalProfit ?? resData?.totalProfit ?? null)
                : (resData?.totalDeliveryFees ?? resData?.TotalDeliveryFees ?? resData?.totalFees ?? null);

          // Fire current and previous period requests simultaneously.
          const [currentRes, previousRes] = await Promise.all([
            fetch(buildUrl(filter === "all" ? null : startDate, now), { headers }),
            filter !== "all"
              ? fetch(buildUrl(prevStartDate, prevEndDate), { headers })
              : Promise.resolve(null),
          ]);

          if (currentRes?.ok) {
            const d = await currentRes.json();
            const v = extractValue(d);
            if (v !== null) {
              val = v;
              writeCache(type, filter, v); // persist for next visit
            }
          }
          if (previousRes?.ok) {
            const d = await previousRes.json();
            const v = extractValue(d);
            if (v !== null) prevVal = v;
          }
        } catch (e) {
          console.error("API Error in StatCard:", e);
        }
      }

      if (type === 'visits') {
        val = visitsCount;
        prevVal = (visitsCount / (filtered.length || 1)) * (prevFiltered.length || 0.8) || (visitsCount * 0.9);
      }

      let trend = 0;
      if (prevVal > 0) trend = ((val - prevVal) / prevVal) * 100;
      else if (val > 0) trend = 100;

      const dailyMap = {};
      filtered.slice(-15).forEach(o => {
        const d = parseOrderDate(o.orderDate || o.OrderDate || o.createdAt);
        if (d) {
          const key = d.toLocaleDateString();
          dailyMap[key] = (dailyMap[key] || 0) + (type === 'sales' ? getOrderTotal(o) : 1);
        }
      });
      let spark = Object.values(dailyMap).map(v => ({ val: v }));
      if (spark.length < 2) spark = [{ val: 0 }, { val: 10 }, { val: 5 }, { val: 15 }];

      if (isMounted) {
        setData({ value: val, prevValue: prevVal, trend, sparkData: spark });
        setLoading(false);
      }
    };

    const localValue = computeLocalValue(allOrders, filter);
    setData((prev) => ({ ...prev, value: localValue }));

    fetchDataAndTrends();
    return () => { isMounted = false; };
  }, [filter, allOrders, visitsCount, type, baseUrl, tenantId]);

  const isPositive = data.trend >= 0;

  return (
    <div className={`rounded-[2rem] p-6 relative overflow-hidden transition-all duration-500 border flex flex-col justify-between h-[240px] group
        ${isDark ? "bg-[#1e293b] border-[#334155] " : "bg-white border-gray-100 shadow-sm "}
    `}>

      {/* Header Section */}
      <div className="flex justify-between items-center z-20 relative">
        <span className={`text-[10px] font-bold uppercase tracking-wider ${isDark ? "text-gray-400" : "text-gray-500"}`}>
          {title}
        </span>
        <div className={`p-2.5 rounded-xl transition-all duration-500  shadow-sm ${colorScheme.bg} ${colorScheme.text}`}>
          <Icon size={18} />
        </div>
      </div>

      {/* Main Value Area */}
      <div className="relative z-10 -mt-4">
        <div className="flex flex-col gap-1">
          <div className="flex items-baseline gap-1.5 flex-wrap">
            <h3 className={`text-3xl font-bold tracking-tight transition-opacity ${loading ? "opacity-50" : "opacity-100"} ${isDark ? "text-white" : "text-slate-800"}`}>
              {formatNumber(Math.round(data.value))}
            </h3>
            {(type !== 'visits') && <span className="text-[10px] font-medium text-gray-400">{isArabic ? "د.ع" : "IQD"}</span>}


          </div>

          {/* Premium Subtext Trend Indicator for Sales and Profits */}
          {(type === 'sales' || type === 'profits') && (
            <div className="flex items-center gap-1.5 mt-0.5">
              <div className={`flex items-center gap-0.5 px-1.5 py-0.5 rounded-md text-[11px] font-bold ${isPositive
                ? (isDark ? "bg-emerald-500/10 text-emerald-400" : "bg-emerald-50 text-emerald-600")
                : (isDark ? "bg-rose-500/10 text-rose-400" : "bg-rose-50 text-rose-600")
                }`}>
                <span dir="ltr">{isPositive ? "+" : ""}{data.trend.toFixed(1)}%</span>
                <svg
                  className={`w-3 h-3 transform transition-transform duration-500 ${isPositive ? "" : "rotate-180"}`}
                  fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}
                >
                  <path strokeLinecap="round" strokeLinejoin="round" d="M5 15l7-7 7 7" />
                </svg>
              </div>
              <span className={`text-[10px] sm:text-[11px] font-medium ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                {isArabic ? (
                  filter === '1d' ? 'مقارنة باليوم السابق' :
                    filter === '7d' ? 'مقارنة بالأسبوع السابق' :
                      filter === '30d' ? 'مقارنة بالشهر السابق' :
                        filter === '60d' ? 'مقارنة بآخر 60 يوم' :
                          filter === '90d' ? 'مقارنة بآخر 90 يوم' :
                            filter === '1y' ? 'مقارنة بالسنة السابقة' :
                              'مقارنة بكل الفترة'
                ) : (
                  filter === '1d' ? 'vs Previous Day' :
                    filter === '7d' ? 'vs Previous Week' :
                      filter === '30d' ? 'vs Previous Month' :
                        filter === '60d' ? 'vs Previous 60 Days' :
                          filter === '90d' ? 'vs Previous 90 Days' :
                            filter === '1y' ? 'vs Previous Year' :
                              'vs All Time'
                )}
              </span>
            </div>
          )}
        </div>
      </div>

      {/* Bold & Balanced Spark Bars - High Visibility */}
      <div className="absolute top-[135px] left-4 right-4 h-10 flex items-end justify-between opacity-60 group-hover:opacity-100 transition-all duration-500 z-0">
        {(data.sparkData.length > 0 ? data.sparkData.slice(-15) :
          Array.from({ length: 15 }, (_, i) => ({ val: [5, 12, 8, 15, 10, 20, 14, 8, 12, 6, 18, 11, 5, 9, 13][i] }))
        ).map((item, index, arr) => {
          const maxVal = Math.max(...arr.map(i => i.val), 1);
          const heightPercent = (item.val / maxVal) * 100;
          return (
            <motion.div
              key={index}
              initial={{ height: 0 }}
              animate={{ height: `${Math.max(heightPercent, 15)}%` }}
              transition={{ duration: 0.8, delay: index * 0.03 }}
              className="w-[10px] rounded-full opacity-80"
              style={{
                backgroundColor: colorScheme.stroke,
                boxShadow: `0 0 10px ${colorScheme.stroke}33`
              }}
            />
          );
        })}
      </div>

      {/* Footer Section */}
      <div className="mt-auto pt-4 flex justify-between items-center z-30 relative w-full border-t border-gray-500/5 hover:border-gray-500/20 transition-colors">
        <button
          onClick={() => navigate('/admin-store/reports/sales')}
          className={`text-[10px] font-bold transition-colors ${isDark ? "text-gray-500 " : "text-gray-400 "}`}
        >
          {isArabic ? "عرض التفاصيل" : "view Details"}
        </button>

        <div className="relative flex items-center">
          <select
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            className={`appearance-none outline-none border-none pr-6 pl-2 py-1 rounded-lg text-[10px] font-bold cursor-pointer transition-all
    ${isDark
                ? "bg-slate-800 text-white active:bg-slate-700" // خلفية داكنة صريحة للـ select
                : "bg-gray-50 text-gray-500 active:bg-gray-100"
              }`}
          >
            <option value="1d" style={{ backgroundColor: isDark ? '#1e293b' : '#fff', color: isDark ? '#fff' : '#000' }}>
              {isArabic ? "اليوم" : "Today"}
            </option>
            <option value="7d" style={{ backgroundColor: isDark ? '#1e293b' : '#fff', color: isDark ? '#fff' : '#000' }}>
              {isArabic ? "الأسبوع" : "This Week"}
            </option>
            <option value="30d" style={{ backgroundColor: isDark ? '#1e293b' : '#fff', color: isDark ? '#fff' : '#000' }}>
              {isArabic ? "آخر 30 يوم" : "Last 30 Days"}
            </option>
            <option value="60d" style={{ backgroundColor: isDark ? '#1e293b' : '#fff', color: isDark ? '#fff' : '#000' }}>
              {isArabic ? "آخر 60 يوم" : "Last 60 Days"}
            </option>
            <option value="90d" style={{ backgroundColor: isDark ? '#1e293b' : '#fff', color: isDark ? '#fff' : '#000' }}>
              {isArabic ? "آخر 90 يوم" : "Last 90 Days"}
            </option>
            <option value="1y" style={{ backgroundColor: isDark ? '#1e293b' : '#fff', color: isDark ? '#fff' : '#000' }}>
              {isArabic ? "سنة" : "Year"}
            </option>
            <option value="all" style={{ backgroundColor: isDark ? '#1e293b' : '#fff', color: isDark ? '#fff' : '#000' }}>
              {isArabic ? "كل الفترة" : "All Time"}
            </option>
          </select>
          <FiChevronDown className={`pointer-events-none absolute ${isArabic ? 'left-1' : 'right-1'} text-current opacity-40`} size={10} />
        </div>
      </div>
    </div>
  );
};

export default function SalesPage() {
  const { theme, lang } = useUI();
  const isDark = theme === "dark";
  const isArabic = lang === "ar";
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [visits, setVisits] = useState(0);

  const baseUrl = (
    process.env.NODE_ENV === "development"
      ? "/api"
      : (process.env.REACT_APP_API_BASE_URL || "https://madar-api.madar.iq/api")
      ).replace(/\/+$/, '');
  const tenantId = localStorage.getItem("tenantId");

  useEffect(() => {
    const fetchData = async () => {
      if (!tenantId) return;
      try {
        const token = localStorage.getItem("authToken") || localStorage.getItem("token") || "";
        const headers = token ? { "Authorization": `Bearer ${token}`, "Accept": "application/json" } : { "Accept": "application/json" };

        // 1. Fetch Orders
        const res = await fetch(`${baseUrl}/GetTenantOrders?tenantId=${tenantId}&statusId=-1`, {
          headers
        });

        if (res.ok) {
          const contentType = res.headers.get("content-type");
          if (contentType && contentType.includes("application/json")) {
            const data = await res.json();
            if (Array.isArray(data)) setOrders(data);
          } else {
            setOrders([]);
          }
        }

        // 2. Fetch Visits
        const vRes = await fetch(`${baseUrl}/VisitsCount?tenantId=${tenantId}`);
        if (vRes.ok) {
          const vContentType = vRes.headers.get("content-type");
          if (vContentType && vContentType.includes("application/json")) {
            const vData = await vRes.json();
            setVisits(Number(vData.visitsCount || 0));
          } else {
            setVisits(0);
          }
        }
      } catch (e) {
        console.error("Fetch error:", e);
      }
    };
    fetchData();
  }, [baseUrl, tenantId]);

  const colorSchemes = [
    { bg: "bg-blue-500/10", text: "text-blue-600", stroke: "#3b82f6" },
    { bg: "bg-emerald-500/10", text: "text-emerald-600", stroke: "#10b981" },
    { bg: "bg-orange-500/10", text: "text-orange-600", stroke: "#f59e0b" },
    { bg: "bg-indigo-500/10", text: "text-indigo-600", stroke: "#6366f1" },
  ];

  const cards = [
    { key: 'sales', type: 'sales', title: isArabic ? "إجمالي المبيعات" : "Total Sales", icon: FiDollarSign },
    { key: 'profits', type: 'profits', title: isArabic ? "صافي الأرباح" : "Net Profits", icon: FiPieChart },
    { key: 'delivery', type: 'delivery', title: isArabic ? "رسوم التوصيل" : "Delivery Fees", icon: FiTruck },
    { key: 'visits', type: 'visits', title: isArabic ? "عدد الزيارات" : "Total Visits", icon: FiEye },
  ];

  return (
    <div dir={isArabic ? "rtl" : "ltr"} className="w-full p-4 lg:p-8 space-y-8">
      <section className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
        {cards.map((c, i) => {
          const { key, ...statProps } = c;
          return (
            <motion.div key={key} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4, delay: i * 0.1 }}>
              <StatCard {...statProps} allOrders={orders} visitsCount={visits} isArabic={isArabic} isDark={isDark} navigate={navigate} colorScheme={colorSchemes[i]} baseUrl={baseUrl} tenantId={tenantId} />
            </motion.div>
          );
        })}
      </section>
    </div>
  );
}
